import { Button, Input, useSnackbar } from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Palette, Send, Timer } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  communicationSettingsApi,
  getApiError,
  type CommunicationSettings as Settings,
  type CommunicationSettingsView,
} from '../lib/api.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { PageHeader } from './page-title.js';

/// Os ajustes de comunicação da plataforma.
///
/// Existe porque a tela de Modelos criou uma pergunta que não tinha resposta: ela lista
/// `{{expiresAt}}` como variável disponível, e até aqui a única forma de mudar esse prazo era
/// editar o código. Junto vem o remetente, que simplesmente não existia — nunca precisou existir
/// enquanto o provedor de desenvolvimento gravava em vez de mandar.
export function CommunicationSettings() {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const query = useQuery({
    queryKey: ['communication-settings'],
    queryFn: communicationSettingsApi.read,
  });

  // O formulário é rascunho local: o que a API trouxe entra uma vez, e a partir daí quem manda é
  // quem está digitando. `key` na consulta faria a tela remontar e apagar a edição em andamento.
  const [edits, setEdits] = useState<Partial<Settings>>({});
  const form: Settings | undefined = query.data ? { ...strip(query.data), ...edits } : undefined;

  const save = useMutation({
    mutationFn: (settings: Settings) => communicationSettingsApi.save(settings),
    onSuccess: (updated) => {
      client.setQueryData(['communication-settings'], updated);
      setEdits({});
      snackbar.success('Ajustes salvos. Valem já na próxima mensagem, sem reiniciar nada.');
    },
    onError: (error) => snackbar.failure(getApiError(error)),
  });

  if (query.error) return <ErrorCard message={getApiError(query.error)} />;
  if (!form || !query.data) return <LoadingCard label="Carregando os ajustes" />;
  const { bounds } = query.data;

  function update<K extends keyof Settings>(field: K, value: Settings[K]) {
    setEdits((current) => ({ ...current, [field]: value }));
  }

  return (
    <section aria-labelledby="communication-settings-title" className="max-w-2xl">
      <PageHeader
        trail="Plataforma / Comunicação / Ajustes"
        id="communication-settings-title"
        title="Ajustes de comunicação"
        description="De quem os e-mails vêm, por quanto tempo cada link vale e como a mensagem se apresenta."
      />

      <form
        className="space-y-8"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(form);
        }}
      >
        <Group icon={Send} title="Remetente" description="Quem aparece como autor da mensagem.">
          <Input
            label="Nome"
            value={form.senderName}
            onChange={(event) => update('senderName', event.target.value)}
            hint="É o que a pessoa vê na lista da caixa de entrada, antes de abrir."
          />
          <Input
            label="Endereço"
            type="email"
            value={form.senderAddress}
            onChange={(event) => update('senderAddress', event.target.value)}
            hint="O domínio precisa ser um que o provedor de envio conheça. Endereço bonito em domínio não autenticado sai e cai em spam, o que é pior do que não sair."
          />
          <Input
            label="Endereço de resposta (opcional)"
            type="email"
            value={form.replyTo ?? ''}
            onChange={(event) => update('replyTo', event.target.value || null)}
            hint="Para onde vai quem responder assim mesmo — e alguém sempre responde. Em branco, a resposta volta para o endereço de envio."
          />
          {/* Um aviso que a tela não tem como dar sozinha: ela sabe validar o formato do endereço,
              não sabe se o DNS do domínio autoriza quem envia. */}
          <p className="flex gap-2 rounded-xl border border-info/30 bg-info/10 px-4 py-3 text-xs leading-5 text-info">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              Trocar o endereço aqui não configura o provedor: o domínio precisa ter SPF, DKIM e
              DMARC apontando para quem envia. Sem isso a mensagem sai — e cai em spam.
            </span>
          </p>
        </Group>

        <Group
          icon={Timer}
          title="Prazos"
          description="Por quanto tempo cada link continua funcionando. É o valor que aparece como {{expiresAt}} nos modelos."
        >
          {/* Curto demais é armadilha para quem abriu o e-mail no celular e foi terminar no
              computador; longo demais transforma um e-mail encaminhado sem pensar numa chave de
              acesso que ainda funciona semanas depois. */}
          <Deadline
            label="Recuperação de senha"
            unit="minutos"
            value={form.passwordResetTtlMinutes}
            bounds={bounds.passwordResetTtlMinutes}
            onChange={(value) => update('passwordResetTtlMinutes', value)}
          />
          <Deadline
            label="Convite de proprietário"
            unit="horas"
            value={form.ownerInvitationTtlHours}
            bounds={bounds.ownerInvitationTtlHours}
            onChange={(value) => update('ownerInvitationTtlHours', value)}
          />
          <Deadline
            label="Convite de membro"
            unit="horas"
            value={form.memberInvitationTtlHours}
            bounds={bounds.memberInvitationTtlHours}
            onChange={(value) => update('memberInvitationTtlHours', value)}
          />
        </Group>

        <Group
          icon={Palette}
          title="Identidade da mensagem"
          description="A moldura em volta do texto: o cabeçalho e a cor. O corpo de cada e-mail é editado em Modelos."
        >
          <Input
            label="Nome exibido no topo do e-mail"
            value={form.brandName}
            onChange={(event) => update('brandName', event.target.value)}
          />
          <div className="flex items-end gap-3">
            <Input
              label="Cor de destaque"
              value={form.brandAccent}
              onChange={(event) => update('brandAccent', event.target.value)}
              className="flex-1"
              hint="No formato #rrggbb."
            />
            <span
              aria-hidden="true"
              className="mb-8 size-11 shrink-0 rounded-xl border border-surface-border"
              style={{ backgroundColor: form.brandAccent }}
            />
          </div>
        </Group>

        <div className="flex items-center gap-3 border-t border-surface-border pt-6">
          <Button loading={save.isPending}>
            <Check className="size-4" />
            Salvar ajustes
          </Button>
        </div>
      </form>
    </section>
  );
}

function Group({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Send;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-3 flex items-start gap-2.5">
        <Icon className="mt-0.5 size-4 shrink-0 text-brand-400" aria-hidden="true" />
        <span>
          <span className="block text-sm font-semibold text-content">{title}</span>
          <span className="mt-0.5 block text-xs leading-5 text-content-muted">{description}</span>
        </span>
      </legend>
      {children}
    </fieldset>
  );
}

function Deadline({
  label,
  unit,
  value,
  bounds,
  onChange,
}: {
  label: string;
  unit: string;
  value: number;
  bounds: { min: number; max: number };
  onChange: (value: number) => void;
}) {
  const outside = value < bounds.min || value > bounds.max;
  return (
    <div>
      <div className="flex items-end gap-3">
        <Input
          label={label}
          type="number"
          inputMode="numeric"
          min={bounds.min}
          max={bounds.max}
          value={String(value)}
          onChange={(event) => onChange(Number(event.target.value))}
          className="flex-1"
          {...(outside
            ? { error: `Precisa ficar entre ${String(bounds.min)} e ${String(bounds.max)}.` }
            : {})}
        />
        <span className="mb-3 shrink-0 text-sm text-content-muted">{unit}</span>
      </div>
      {!outside ? (
        <p className="mt-1 text-xs text-content-faint">
          Entre {bounds.min} e {bounds.max} {unit}.
        </p>
      ) : null}
    </div>
  );
}

/// A visão da API traz os limites junto; o formulário guarda só o que é editável. Sem isto, salvar
/// devolveria os limites de volta para o servidor como se fossem configuração.
function strip(view: CommunicationSettingsView): Settings {
  return {
    senderName: view.senderName,
    senderAddress: view.senderAddress,
    replyTo: view.replyTo,
    passwordResetTtlMinutes: view.passwordResetTtlMinutes,
    ownerInvitationTtlHours: view.ownerInvitationTtlHours,
    memberInvitationTtlHours: view.memberInvitationTtlHours,
    brandName: view.brandName,
    brandAccent: view.brandAccent,
  };
}
